import dayjs, { Dayjs } from 'dayjs'
import { ReportDescriptor, ReportFetchParams } from './ReportDescriptors'

import { GridColDef, GridRowsProp } from '@mui/x-data-grid-premium'
import { EthAddr } from '@shared/SchemaTypes'
import minMax from 'dayjs/plugin/minMax'
import { JsonRpcProvider } from 'ethers'
import { TimeStep } from '@shared/Enums'
import { reportColumnsForDescriptor } from './ReportingColumns'

dayjs.extend(minMax)
// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
export class ReportRowGeneratorClass {
  constructor(props: {
    reportDescriptor: ReportDescriptor
    dateRange?: [Dayjs, Dayjs]
    providerReadOnly: JsonRpcProvider
    serverApiBaseUrl: string
    restAPIKey: string
    poolAddr: EthAddr
    lenderAddr: EthAddr | undefined
    stageName?: string
    endDateTime?: Dayjs
    timeStep?: TimeStep
  }) {
    this.reportDescriptor = props.reportDescriptor
    this.rows = []
    this.loading = false
    this.dateRange = props.dateRange
    this.endDateTime = props.endDateTime
    this.timeStep = props.timeStep
    this.serverApiBaseUrl = props.serverApiBaseUrl
    this.restAPIKey = props.restAPIKey
    this.poolAddr = props.poolAddr
    this.lenderAddr = props.lenderAddr
    this.providerReadOnly = props.providerReadOnly
    this.stageName = props.stageName
    this.currentFetchProgress = undefined
    this.errorMessageCallback = undefined
    this.infoMessageCallback = undefined
  }
  errorMessageCallback?: (errorMessage: string) => void
  infoMessageCallback?: (infoMessage: string) => void

  reportDescriptor: ReportDescriptor
  rows: GridRowsProp
  loading: boolean
  dateRange?: [Dayjs, Dayjs]
  serverApiBaseUrl: string
  restAPIKey: string
  poolAddr: EthAddr
  lenderAddr: EthAddr | undefined
  providerReadOnly: JsonRpcProvider | undefined
  stageName?: string
  endDateTime?: Dayjs
  timeStep?: TimeStep
  currentFetchProgress: string | undefined
  fetchDataErrorMessage: string | undefined
  dateRangeToDayStrings(dateRange?: [Dayjs, Dayjs]): { startDayStr?: string; endDayStr?: string } {
    const startDayStr = dateRange && dateRange.length > 1 ? dateRange[0].format('YYYY-MM-DD') : undefined
    const endDayStr = dateRange && dateRange.length > 1 ? dateRange[1].format('YYYY-MM-DD') : undefined
    return { startDayStr, endDayStr }
  }

  get columns(): GridColDef[] {
    const { columns } = reportColumnsForDescriptor(this.reportDescriptor)
    return columns
  }
  getColumnsWithWidths(columnWidths: Record<string, number>): GridColDef[] {
    const { columns } = reportColumnsForDescriptor(this.reportDescriptor)
    const colsWithWidths = columns.map((col) => {
      if (columnWidths[col.field]) {
        col.width = columnWidths[col.field]
      }
      return col
    })
    return colsWithWidths
  }

  get defaultColumnVisibilityModel(): Record<string, boolean> {
    const { columnVisibilityModel } = reportColumnsForDescriptor(this.reportDescriptor)
    return columnVisibilityModel
  }
  get defaultSortModel(): { field: string; sort: 'asc' | 'desc' | null | undefined }[] {
    return [{ field: 'blockNumber', sort: 'asc' }]
  }
  get defaultColumnWidths(): Record<string, number> {
    const defaultColumnWidths: Record<string, number> = {}
    this.columns.forEach((column) => {
      defaultColumnWidths[column.field] = column.width ?? 150
    })
    return defaultColumnWidths
  }

  get columnOrder(): Record<string, number> {
    const { columnOrder } = reportColumnsForDescriptor(this.reportDescriptor)
    return columnOrder
  }

  refreshData = async () => {
    try {
      if (this.loading) {
        return
      }
      this.loading = true
      this.rows = []
      this.currentFetchProgress = `Fetching data...`
      this.infoMessageCallback?.(this.currentFetchProgress)
      this.fetchDataErrorMessage = ''
      this.errorMessageCallback?.(this.fetchDataErrorMessage ?? 'Unknown error')

      const { startDayStr, endDayStr } = this.dateRangeToDayStrings(this.dateRange)

      const fetcherParams: ReportFetchParams = {
        serverApiBaseUrl: this.serverApiBaseUrl,
        restAPIKey: this.restAPIKey,
        poolAddr: this.poolAddr,
        lenderAddr: this.lenderAddr,
        startDayStr,
        endDayStr,
        providerReadOnly: this.providerReadOnly,
        stageName: this.stageName,
        endDateTime: this.endDateTime,
        timeStep: this.timeStep,
      }

      if (this.dateRange && this.dateRange[0] && this.dateRange[1]) {
        const _rows = []
        // TODO: make this dynamic with a backoff on a timeout
        const DAYS_AT_A_TIME = 16
        let currentEndDay = this.dateRange[1]

        const d1 = currentEndDay.subtract(DAYS_AT_A_TIME - 1, 'days')
        let currentStartDay = dayjs.max(this.dateRange[0], d1)

        let cont = true
        while (cont) {
          const iterationFetchParams: ReportFetchParams = {
            ...fetcherParams,
            startDayStr: currentStartDay.format('YYYY-MM-DD'),
            endDayStr: currentEndDay.format('YYYY-MM-DD'),
          }

          const nextRows = await this.reportDescriptor.fetcher(iterationFetchParams)
          _rows.push(...nextRows)
          this.rows = _rows
          this.currentFetchProgress = `Fetched ${currentStartDay.format('YYYY-MM-DD')} to ${this.dateRange[1].format('YYYY-MM-DD')}`

          this.infoMessageCallback?.(this.currentFetchProgress)

          currentEndDay = currentStartDay.subtract(1, 'day')
          currentStartDay = dayjs.max(this.dateRange[0], currentEndDay.subtract(DAYS_AT_A_TIME - 1, 'days'))
          if (currentEndDay.isBefore(this.dateRange[0])) {
            cont = false
          }
        }
        this.infoMessageCallback?.('done')
      } else {
        const _rows = await this.reportDescriptor.fetcher(fetcherParams)
        this.rows = _rows
        this.infoMessageCallback?.('done')
      }

      this.loading = false
    } catch (error: any) {
      this.loading = false
      this.fetchDataErrorMessage = error.message
      this.errorMessageCallback?.(this.fetchDataErrorMessage ?? 'Unknown error')
      this.currentFetchProgress = ''
      this.infoMessageCallback?.(this.currentFetchProgress)
    } finally {
      this.loading = false
    }
  }
}
